import { Button } from '@heidi/design-system/button';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '@heidi/design-system/drawer';
import { Separator } from '@heidi/design-system/separator';
import { Tooltip, TooltipContent, TooltipTrigger } from '@heidi/design-system/tooltip';
import { TypographyCaption } from '@heidi/design-system/typography';
import { useTranslations } from '@heidi/runtime/i18n';
import { LayoutGrid, SlashSquare, Zap } from 'lucide-react';
import type { ProtocolsMenuProps } from './protocols-button.types';

export type ProtocolsMobileDrawerProps = ProtocolsMenuProps;

export const ProtocolsMobileDrawer = ({
  isOpen,
  onOpenChange,
  triggerButton,
  tooltipText,
  pinnedChips,
  onPinnedChipClick,
  onBrowseAllClick,
  activeCuratedProtocols,
  onProtocolSelect,
}: ProtocolsMobileDrawerProps) => {
  const { t } = useTranslations();
  const hasPinnedChips = pinnedChips.length > 0;
  const hasCuratedProtocols = activeCuratedProtocols.length > 0;

  return (
    <Drawer open={isOpen} onOpenChange={onOpenChange}>
      <Tooltip delayDuration={400}>
        <TooltipTrigger asChild>
          <DrawerTrigger asChild>{triggerButton}</DrawerTrigger>
        </TooltipTrigger>
        {!isOpen && (
          <TooltipContent side="top" sideOffset={8}>
            {tooltipText}
          </TooltipContent>
        )}
      </Tooltip>
      <DrawerContent className="max-h-[80vh]">
        <DrawerHeader className="sr-only">
          <DrawerTitle>{t('floatingAskHeidiShortcuts.shortcuts')}</DrawerTitle>
        </DrawerHeader>
        <div className="flex max-h-96 flex-col overflow-y-auto px-3 pb-6 pt-2">
          <Button
            variant="ghost"
            size="md"
            className="justify-start gap-3"
            onClick={onBrowseAllClick}
          >
            <LayoutGrid size={16} className="text-text-secondary" />
            {t('floatingAskHeidiShortcuts.seeAllShortcuts')}
          </Button>
          {hasPinnedChips && (
            <>
              <Separator className="my-2" />
              {pinnedChips.map((chip) => (
                <Button
                  key={chip.id}
                  variant="ghost"
                  size="md"
                  className="justify-start gap-3"
                  onClick={() => {
                    onPinnedChipClick?.(chip);
                    onOpenChange(false);
                  }}
                >
                  <SlashSquare size={16} className="text-text-secondary" />
                  <span className="truncate">{chip.label}</span>
                </Button>
              ))}
            </>
          )}
          {hasCuratedProtocols && (
            <>
              <Separator className="my-2" />
              <TypographyCaption className="px-3 py-2 text-text-tertiary">
                {t('floatingAskHeidiShortcuts.protocols')}
              </TypographyCaption>
              {activeCuratedProtocols.map((protocol) => (
                <Button
                  key={protocol.id}
                  variant="ghost"
                  size="md"
                  className="justify-start gap-3"
                  onClick={() => {
                    onProtocolSelect(protocol.shorthand);
                    onOpenChange(false);
                  }}
                >
                  <Zap size={16} className="text-text-secondary" />
                  <span className="truncate">{protocol.name}</span>
                </Button>
              ))}
            </>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
};
